import React, { SelectHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      options,
      placeholder = 'Select an option',
      size = 'md',
      className,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const sizeClasses: Record<string, string> = {
      sm: 'h-[36px] py-2 px-3 text-xs',
      md: 'h-[42px] py-2.5 px-3.5 text-sm',
      lg: 'h-[48px] py-3 px-4 text-base',
    };

    const hasError = !!error;

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            className={cn(
              'block text-sm font-medium mb-2',
              hasError ? 'text-error-500' : 'text-monday-text-muted',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {label}
            {required && <span className="form-required-dot"></span>}
          </label>
        )}

        {/* Select Input */}
        <div className="relative">
          <select
            ref={ref}
            disabled={disabled}
            className={cn(
              'w-full appearance-none rounded-xl border bg-white/95 shadow-xs',
              'text-monday-text transition-all duration-200',
              'focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)]',
              'disabled:bg-monday-header disabled:cursor-not-allowed disabled:opacity-60',
              sizeClasses[size],
              hasError
                ? 'border-error-500 focus:border-error-500 focus:shadow-[0_0_0_4px_rgba(199,51,64,0.16)]'
                : 'border-monday-border focus:border-primary-500 hover:border-neutral-300',
              'pr-10',
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {/* Dropdown Icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className={cn(
                'w-4 h-4',
                hasError ? 'text-error-500' : 'text-monday-text-muted'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Hint or Error */}
        {(hint || error) && (
          <p className={cn('mt-1 text-xs', hasError ? 'text-error-500' : 'text-monday-text-muted')}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
